import { useState } from 'react';
import { ArrowRight, ArrowLeft, Check, Upload, X as XIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { motion, AnimatePresence } from 'motion/react';
import { submitQuote, uploadImage } from '../lib/api';
import type { QuoteData } from '../lib/api';

interface QuickQuoteStepperProps {
  serviceSlug: string;
  serviceName: string;
  onClose?: () => void;
}

export function QuickQuoteStepper({
  serviceSlug,
  serviceName,
  onClose,
}: QuickQuoteStepperProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    layout: '',
    photoUrl: '',
    zip: '',
    name: '',
    phone: '',
    email: '',
    consent: false,
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  const totalSteps = 4;

  const updateField = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));

    // Upload immediately
    setIsUploading(true);
    try {
      const uploadResponse = await uploadImage(file);
      updateField('photoUrl', uploadResponse.url);
    } catch (err) {
      setError('Failed to upload image. Please try again.');
      console.error('Image upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview('');
    updateField('photoUrl', '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const quoteData: QuoteData = {
        service: serviceSlug,
        layout: formData.layout,
        photoUrl: formData.photoUrl,
        zip: formData.zip,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        referralUrl: document.referrer || window.location.href,
      };

      const response = await submitQuote(quoteData);
      setTicketId(response.ticketId);
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit quote. Please try again or call us directly.');
      console.error('Quote submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-8 text-center max-w-md mx-auto"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="mb-2">Quote Request Received!</h3>
        <p className="text-muted-foreground mb-4">
          Ticket ID: <strong>{ticketId}</strong>
        </p>
        <p className="mb-6">
          We'll call you within 1 business day to discuss your project and provide a detailed quote.
        </p>
        <div className="flex flex-col gap-3">
          <Button asChild className="w-full">
            <a href="tel:6468999891">Call Us Now: 646-899-9891</a>
          </Button>
          {onClose && (
            <Button variant="outline" onClick={onClose} className="w-full">
              Close
            </Button>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 max-w-2xl mx-auto">
      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex justify-between mb-2">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`flex-1 h-2 rounded-full mx-1 transition-colors ${
                i + 1 <= step ? 'bg-primary' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <p className="text-sm text-muted-foreground">
          Step {step} of {totalSteps}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <h3 className="mb-4">Select Your {serviceName} Type</h3>
              <div className="grid grid-cols-1 gap-3">
                {getLayoutOptions(serviceSlug).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      updateField('layout', option);
                      setStep(2);
                    }}
                    className={`p-4 border-2 rounded-lg text-left transition-all hover:border-primary ${
                      formData.layout === option
                        ? 'border-primary bg-primary/5'
                        : 'border-border'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <h3 className="mb-4">Add a Photo of Your Project</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Upload a photo to help us better understand your project (optional)
              </p>

              {!photoPreview ? (
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors">
                  <input
                    type="file"
                    id="photo-upload"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="photo-upload"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <Upload className="w-12 h-12 text-muted-foreground" />
                    <span className="text-sm font-medium">Click to upload photo</span>
                    <span className="text-xs text-muted-foreground">
                      PNG, JPG up to 10MB
                    </span>
                  </label>
                </div>
              ) : (
                <div className="relative border rounded-lg overflow-hidden">
                  <img
                    src={photoPreview}
                    alt="Project preview"
                    className="w-full h-64 object-cover"
                  />
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                  {isUploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="text-white text-sm">Uploading...</div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <h3 className="mb-4">Location</h3>
              <div>
                <Label htmlFor="zip">ZIP Code</Label>
                <Input
                  id="zip"
                  type="text"
                  placeholder="10001"
                  maxLength={5}
                  value={formData.zip}
                  onChange={(e) => updateField('zip', e.target.value)}
                  required
                />
              </div>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <h3 className="mb-4">Contact Information</h3>
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(646) 899-9891"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  required
                />
              </div>
              <div className="flex items-start gap-2 pt-2">
                <Checkbox
                  id="consent"
                  checked={formData.consent}
                  onCheckedChange={(checked) =>
                    updateField('consent', checked as boolean)
                  }
                  required
                />
                <Label htmlFor="consent" className="text-sm leading-tight cursor-pointer">
                  I agree to receive a callback from NY Glass. No spam. 1-business-day response.
                </Label>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error message */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex gap-3 mt-8">
          {step > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back
            </Button>
          )}
          {step < totalSteps ? (
            <Button
              type="button"
              onClick={() => setStep(step + 1)}
              className="flex-1"
              disabled={
                (step === 1 && !formData.layout) ||
                (step === 3 && !formData.zip)
              }
            >
              Next
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          ) : (
            <Button type="submit" className="flex-1" disabled={!formData.consent || isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

function getLayoutOptions(serviceSlug: string): string[] {
  const options: Record<string, string[]> = {
    'shower-doors': [
      'Single Door',
      'Door + Panel',
      'Neo-Angle',
      'Inline',
      'Tub Slider',
      'Steam Enclosure',
    ],
    'mirrors': [
      'Single Panel',
      'Multi-Panel Wall',
      'Gym Wall',
      'Dance Studio',
      'Bathroom Mirror',
      'Custom Size',
    ],
    'glass-railings': [
      'Stair Railing',
      'Balcony Railing',
      'Deck Railing',
      'Mezzanine Guard',
      'Lobby Barrier',
    ],
    'glass-tabletops-shelves': [
      'Table Top Protector',
      'Dining Table',
      'Coffee Table',
      'Desk Surface',
      'Floating Shelf',
      'Cabinet Insert',
    ],
    'storefronts': [
      'Storefront Glass',
      'Entry Door',
      'Sliding Door',
      'Pivot Door',
      'Emergency Repair',
      'Full Installation',
    ],
  };

  return options[serviceSlug] || ['Standard', 'Custom'];
}
